import React, { useEffect } from 'react';
import { gql, useQuery } from '@apollo/client';
import { handleError } from '@domain-group/fe-helper';
import mapReviews from '../../utils/map-recommendation-reviews';
import ReviewList from '../shared/recommendations/reviews-list';
import type {
  AgencyRecommendationsFragment,
  AgencyByAgencyIdQuery,
} from '../../generated/graphql';
import type { TrackingCallbacks } from '../../@types';
import { useReviewsContext } from '../recommendations-enquiry-section';

type AgencyRecommendationsWithPaginationProps = TrackingCallbacks & {
  agencyId: number;
  starRatingsEnabled?: boolean;
};

export const QUERY = gql`
  query agencyRecommendationPaginationByAgencyId(
    $agencyId: Int!
    $pageSize: Int
    $pageNumber: Int
  ) {
    agency(agencyId: $agencyId) {
      recommendations(pageSize: $pageSize, pageNumber: $pageNumber) {
        agencyProfileUrl
        reviews {
          title
          description
          reviewedOn
          reviewType
          starRating
          agentReviewTrackingPixelUrl
          agencyReviewTrackingPixelUrl
          reviewUrl
          bedrooms
          bathrooms
          carparks
          streetAddress
          suburb
          state
          postCode
        }
      }
    }
  }
`;

export const AgencyRecommendationsWithPagination = ({
  agencyId,
  onPageRendered,
  onVendorRecoViewAll,
  onVendorRecoRendered,
  onVendorRecoImpression,
  onVendorRecoModalViewMore,
  onVendorRecoShowMore,
  starRatingsEnabled = false,
}: AgencyRecommendationsWithPaginationProps): JSX.Element | null => {
  const { setHasReviews, reviewPageNumber, reviewPageSize } =
    useReviewsContext();
  const { data, error } = useQuery<AgencyByAgencyIdQuery>(QUERY, {
    variables: {
      agencyId,
      pageSize: reviewPageSize,
      pageNumber: reviewPageNumber,
    },
  });

  useEffect(() => {
    if (data?.agency?.recommendations?.reviews?.length) {
      setHasReviews(true);
    }
  }, [data, setHasReviews]);

  if (error) {
    handleError(error);
    return <div data-testid="fe-faa-agency-recommendations-error" />;
  }

  if (data?.agency?.recommendations?.reviews?.length) {
    const reviews = mapReviews(data.agency.recommendations.reviews);
    return (
      <ReviewList
        profileUrl={data.agency.recommendations.agencyProfileUrl || ''}
        reviews={reviews}
        onPageRendered={() => {
          onPageRendered(data as AgencyRecommendationsFragment);
        }}
        onVendorRecoViewAll={() => {
          onVendorRecoViewAll(data as AgencyRecommendationsFragment);
        }}
        onVendorRecoRendered={() => {
          onVendorRecoRendered(data as AgencyRecommendationsFragment);
        }}
        onVendorRecoImpression={() => {
          onVendorRecoImpression(data as AgencyRecommendationsFragment);
        }}
        onVendorRecoModalViewMore={() => {
          onVendorRecoModalViewMore(data as AgencyRecommendationsFragment);
        }}
        starRatingsEnabled={starRatingsEnabled}
        onVendorRecoShowMore={() => {
          onVendorRecoShowMore?.(data as AgencyRecommendationsFragment);
        }}
      />
    );
  }
  return null;
};
